import { Link } from 'react-router-dom';
import BetaSignupForm from '../components/BetaSignupForm';
import ThemeToggle from '../components/ThemeToggle';

export default function Landing() {
  const features = [
    {
      title: 'AI Agent Swarms',
      description:
        'Deploy teams of specialized AI agents that collaborate on research, writing, analysis, and monitoring tasks.',
      icon: '🐝',
    },
    {
      title: 'Private Knowledge Base',
      description:
        'Upload documents, PDFs, and URLs to ground agent responses in your own business data.',
      icon: '📚',
    },
    {
      title: 'Website Monitoring',
      description:
        'Track competitor pages, pricing, and news. Get alerted when important content changes.',
      icon: '🔍',
    },
    {
      title: 'Secure & Multi-tenant',
      description:
        'Enterprise-ready tenant isolation, SSO support, and role-based access control.',
      icon: '🛡️',
    },
    {
      title: 'Local + Cloud AI',
      description:
        'Run private local models via Ollama or switch to cloud providers when you need more power.',
      icon: '☁️',
    },
    {
      title: 'Built for Small Business',
      description:
        'Simple pricing, fast setup, and agent templates designed for real-world business workflows.',
      icon: '🏢',
    },
  ];

  const testimonials = [
    {
      quote:
        'InsightSwarm cut our competitive research time by 80%. The agents just keep working while we focus on strategy.',
      author: 'Sarah Chen',
      role: 'Marketing Director',
    },
    {
      quote:
        'Finally, an AI platform that respects our data privacy. Running everything locally is a game changer.',
      author: 'Marcus Johnson',
      role: 'CISO, TechStart Inc.',
    },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-2xl mr-2">🐝</span>
            <span className="text-xl font-bold text-gray-900 dark:text-white">InsightSwarm</span>
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <Link to="/login" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              Sign in
            </Link>
            <span className="px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide bg-yellow-400 text-yellow-900 rounded">
              Beta
            </span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-purple-50 to-white dark:from-gray-800 dark:to-gray-900 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            Autonomous AI Agent Swarms
            <br />
            <span className="text-purple-600">for Small Business</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Research competitors, create content, monitor websites, and answer questions — all with
            teams of private AI agents working together.
          </p>

          <div className="max-w-md mx-auto">
            <BetaSignupForm compact />
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
            Currently in private beta. Request access below.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Everything you need to scale</h2>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              A complete platform for agentic work, grounded in your data.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="p-6 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-lg transition-shadow">
                <div className="text-3xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-gray-50 dark:bg-gray-800 py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
            Loved by early users
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((t) => (
              <div key={t.author} className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm">
                <p className="text-gray-700 dark:text-gray-300 italic mb-4">"{t.quote}"</p>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">{t.author}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beta Signup Section */}
      <section className="py-20 px-4">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Request Beta Access</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            We're currently in private beta. Join the waitlist and we'll get you set up.
          </p>
          <div className="text-left">
            <BetaSignupForm />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-700 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <span className="text-xl mr-2">🐝</span>
            <span className="font-semibold text-gray-900 dark:text-white">InsightSwarm</span>
          </div>
          <div className="flex gap-6 text-sm text-gray-600 dark:text-gray-300">
            <Link to="/login" className="hover:text-gray-900 dark:hover:text-white">
              Sign in
            </Link>
            <a href="/docs" className="hover:text-gray-900 dark:hover:text-white">
              Docs
            </a>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} InsightSwarm. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
